import { createAsyncThunk } from '@reduxjs/toolkit'
import { TenantLoginInput } from '@user/components/Login/typings'
import { ServiceConfig } from '../services/config'
import { clearAuthHeader, get, post, setAuthHeader } from '../services/crud'
import {
  IBranchInfoDTO,
  ImpersonateLoginInput,
  ISalesTeamUserPositionsData,
  IUserResponse,
  IVerifyUserExists,
  RemoteLogin,
} from './typings'

const loginUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.tenant.tenantLogin}${ServiceConfig.tenant.login}`
const verifyActiveUserUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.tenant.tenantLogin}${ServiceConfig.tenant.verifyActiveUser}`
const forgotPasswordUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.user.users}${ServiceConfig.user.forgotPassword}`
const impersonateUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.tenant.tenantLogin}${ServiceConfig.tenant.impersonateLogin}`
const impersonateCodeCheckUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.tenant.tenantLogin}${ServiceConfig.tenant.impersonateCodeCheck}`
const remoteLoginUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.remote.remoteAccess}${ServiceConfig.remote.localToken}`
const getLogoutUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.tenant.tenantLogin}${ServiceConfig.tenant.getLogoutUrl}`
const baseUrl = `${ServiceConfig.baseAPIUrl}/`

export const refreshToken = createAsyncThunk<IUserResponse, void>(
  'login/refreshToken',
  async (_, thunkApi) => {
    const response = await get(
      `${ServiceConfig.baseAPIUrl}${ServiceConfig.tenant.tenantLogin}/refreshToken`,
    )
    if (response.ok) {
      const user: IUserResponse = await response.json()
      setAuthHeader(user.token, user.tokenAge)
      return user
    } else {
      clearAuthHeader()
      return thunkApi.rejectWithValue(await response.json())
    }
  },
)

export const loginUser = createAsyncThunk<IUserResponse, TenantLoginInput>(
  'login/signinexternal',
  async (input, thunkApi) => {
    const response = await post(loginUrl, input, undefined, '/login')
    if (response.ok) {
      const user: IUserResponse = await response.json()
      setAuthHeader(user.token, user.tokenAge)
      return user
    } else {
      clearAuthHeader()
      return thunkApi.rejectWithValue(await response.json())
    }
  },
)

export const forgotPassword = createAsyncThunk<boolean, string>(
  'login/forgotPassword',
  async (email, thunkApi) => {
    const response = await post(forgotPasswordUrl, email)
    if (response.ok) return true
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const impersonateLogin = createAsyncThunk<
  IUserResponse,
  ImpersonateLoginInput
>('login/impersonate', async (input, thunkApi) => {
  const response = await post(impersonateUrl, input)
  if (response.ok) {
    const user: IUserResponse = await response.json()
    setAuthHeader(user.token, user.tokenAge)
    return user
  } else {
    clearAuthHeader()
    return thunkApi.rejectWithValue(await response.json())
  }
})

export const impersonateCodeCheck = createAsyncThunk<
  string,
  ImpersonateLoginInput
>('login/impersonatecodecheck', async (input, thunkApi) => {
  const response = await post(impersonateCodeCheckUrl, input)
  if (response.ok) {
    const user: IUserResponse = await response.json()

    return user.emailAddress
  } else {
    clearAuthHeader()
    return thunkApi.rejectWithValue(await response.json())
  }
})

export const remoteLogin = createAsyncThunk<RemoteLogin>(
  'login/remote',
  async (_, thunkApi) => {
    const response = await get(remoteLoginUrl)
    if (response.ok) {
      const user: RemoteLogin = await response.json()
      setAuthHeader(user.token, user.tokenAge)
      return user
    } else {
      clearAuthHeader()
      return thunkApi.rejectWithValue(await response.json())
    }
  },
)

export const getBranchesInfo = createAsyncThunk<IBranchInfoDTO[]>(
  'login/branches',
  async (_, thunkApi) => {
    const response = await get(`${baseUrl}users/branches`)
    if (response.ok) return await response.json()
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getSalesTeamPositionsData =
  createAsyncThunk<ISalesTeamUserPositionsData>(
    'login/salesTeamPositionsData',
    async (_, thunkApi) => {
      const response = await get(`${baseUrl}users/salesTeamPositionsData`)
      if (response.ok) return await response.json()
      return thunkApi.rejectWithValue(await response.json())
    },
  )

export const verifyActiveUser = createAsyncThunk<IVerifyUserExists, string>(
  'login/verifyActiveUser',
  async (email, thunkApi) => {
    const response = await get(
      `${verifyActiveUserUrl}?email=${encodeURIComponent(email)}`,
    )
    if (response.ok) return await response.json()
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const GetLogoutUrl = createAsyncThunk<string>(
  'login/GetLogoutUrl',
  async (_, thunkApi) => {
    const response = await get(`${getLogoutUrl}`)
    if (response.ok) return await response.json()
    return thunkApi.rejectWithValue(await response.json())
  },
)
